import {AbstractControl, ValidationErrors, ValidatorFn} from '@angular/forms';

/*
 * Length limits on the names a PO is submitted with, and where each one comes from. The workflow
 * API enforces the same limits in content-workflow-cdk/api/validate_names.vtl; keep both in step.
 *
 * - po_name: VTK stores it as the job bundle name (ProjectOffer.name, CharField(max_length=128)).
 *   A longer name is refused when the encode creates its job bundle.
 * - po_name and po_item_id: publish writes both, custom_quote-encoded, into S3 object tags
 *   (content-workflow-cdk/api_publish_po_lambdas/filecopy/index.py), whose values are capped.
 *
 * S3 keys (<prefix>/<po_name>/<po_item_id>/<execution id>/...) stay well within their 1024 bytes
 * under these limits, and the VTK job URL carries only po_name.
 */

/** VTK's limit for the job bundle name, which is the po_name. */
export const PO_NAME_MAX_LENGTH = 128;

/** AWS limit for the value of an S3 object tag. */
export const S3_TAG_VALUE_MAX_LENGTH = 255;

/**
 * Character set that `custom_quote` in the publish lambdas passes through verbatim.
 * Every other byte is escaped as `/XX`, so it costs three characters instead of one.
 */
const S3_TAG_ALLOWED_CHARS = '0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ+-=._:';

/**
 * TypeScript port of `custom_quote` from the publish lambdas. Downstream the `po_item_id` is
 * written into an S3 object tag in this encoded form, so the length that matters for the
 * 255-character tag limit is the length of the *encoded* string, not of what the user typed.
 */
export function customQuote(input: string): string {
  let result = '';
  for (const byte of new TextEncoder().encode(input)) {
    const char = String.fromCharCode(byte);
    result += S3_TAG_ALLOWED_CHARS.includes(char)
      ? char
      : '/' + byte.toString(16).toUpperCase().padStart(2, '0');
  }
  return result;
}

/**
 * Rejects values that no longer fit into an S3 object tag once `custom_quote`-encoded.
 * Reports both lengths so the message can explain why a seemingly short value is too long.
 */
export function s3TagValueLengthValidator(max: number = S3_TAG_VALUE_MAX_LENGTH): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value ?? '').toString();
    if (!value) {
      return null;
    }
    const encodedLength = customQuote(value).length;
    return encodedLength > max
      ? {s3TagValueTooLong: {max, encoded: encodedLength, actual: value.length}}
      : null;
  };
}

/**
 * Every limit a complete po_name breaks: `poNameTooLong` for VTK, `s3TagValueTooLong` for the
 * publish tag. Both carry the composed name, since the user typed only part of it.
 */
export function poNameLimitErrors(poName: string): ValidationErrors | null {
  const errors: ValidationErrors = {};
  if (poName.length > PO_NAME_MAX_LENGTH) {
    errors['poNameTooLong'] = {max: PO_NAME_MAX_LENGTH, actual: poName.length, poName};
  }
  const encodedLength = customQuote(poName).length;
  if (encodedLength > S3_TAG_VALUE_MAX_LENGTH) {
    errors['s3TagValueTooLong'] = {
      max: S3_TAG_VALUE_MAX_LENGTH, encoded: encodedLength, actual: poName.length, poName
    };
  }
  return Object.keys(errors).length ? errors : null;
}

/**
 * For the PO number field of a form that submits `composePoName(form)` as po_name. The field is
 * only part of the name, so the limits are checked on the whole of it; the form must re-run this
 * validator when the other parts (airline, supplier, ...) change.
 */
export function poNumberValidator(composePoName: (form: AbstractControl) => string): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    if (!control.parent || !(control.value ?? '').toString().trim()) {
      return null;
    }
    return poNameLimitErrors(composePoName(control.parent));
  };
}
